import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import './Prescription.scss';

import orderSlice, {
  getCurrentLensType,
  getCurrentOrder,
} from '../../../../store/slices/order.slice';
import cartSlice from '../../../../store/slices/cart.slice';

import LensBuilderType from '../../../../types/lens-builder.type';

import RxInput from '../../../../components/rx-input/RxInput';
import { Button, StepsIndicator } from '../../../../components/ui';
import { PD, Rx } from '../../../../store/interfaces/prescription.interface';
import IrlTool from '../../../../components/irl-tool/IrlTool';
import tooltip from '../../../../constants/prescription-tooltip.constant';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useLocation } from 'react-router';

interface props {
  isRxOnly?: boolean;
}

const Prescription: React.FC<props> = ({ isRxOnly = false }) => {
  const { visionType, lensPackage, prescription } = useSelector(getCurrentLensType);
  const dispatch = useDispatch();
  const { order } = useSelector(getCurrentOrder);
  const pd = prescription?.pupillaryDistance || {};
  const isProgressive = visionType?.pdHeightStatus || isRxOnly;

  const [pdType, setPdType] = useState(pd?.single ? 'singlePd' : 'doublePd');

  const [rx, setRx] = useState<any>(prescription || null);
  const [lens, setLens] = useState<any>(lensPackage || null);

  const [errors, setError] = useState([] as string[]);
  const [isValid, setValidity] = useState(true);

  const history = useHistory();
  const isFormSubmitted = useRef<boolean>(false);

  let formRef: HTMLFormElement;

  const location = useLocation();
  let orderItemId = new URLSearchParams(location.search).get('orderId');

  // useEffect(() => {
  //   if (orderItemId) {
  //     dispatch(orderSlice.actions.deletePrescription());
  //   }
  // }, [orderItemId]);

  useEffect(() => {
    if (lens) dispatch(orderSlice.actions.setLensPackage(lens));
  }, [lens]);

  useEffect(() => {
    if (rx) {
      dispatch(orderSlice.actions.setPrescription(rx));

      if (isRxOnly) {
        // get vision need
        let visionNeed = 'Near';
        let pdHeightStatus = false;

        const {
          left: { sphere: lSphere = 0, addition: lAdd = 0 },
          right: { sphere: rSphere = 0, addition: rAdd = 0 },
        } = rx;

        if (rSphere > 0 || lSphere > 0) {
          visionNeed = 'Far';
        }

        if (rAdd > 0 || lAdd > 0) {
          visionNeed = 'Near to Far';
          pdHeightStatus = true;
        }

        dispatch(
          orderSlice.actions.setVisionNeed({
            name: visionNeed,
            pdHeightStatus: pdHeightStatus,
            hasLens: false,
          }),
        );
      }
    }
  }, [isRxOnly, rx]);

  useEffect(() => {
    // validate if order is ready to be added in cart
    if (visionType && lensPackage && prescription) {
      if (orderItemId) {
        dispatch(cartSlice.actions.updateOrder(order));
      } else {
        dispatch(cartSlice.actions.addOrder(order));
      }
      dispatch(orderSlice.actions.resetCurrentOrder());
      history.push('/cart');
    }
  }, [visionType, lensPackage, prescription]);

  const updatePrescription = () => {
    const formData: any = new FormData(formRef);
    let leftRx: Rx = {};
    let rightRx: Rx = {};
    let pdData: PD = {};

    for (var [key, value] of formData.entries()) {
      const rxValue = parseFloat(value) || 0;

      // use spread operator to fix issue on extensible
      if (key === 'rightSphere') rightRx = { ...rightRx, sphere: rxValue };
      else if (key === 'leftSphere') leftRx = { ...leftRx, sphere: rxValue };
      else if (key === 'rightCylinder' && value !== '') rightRx = { ...rightRx, cylinder: rxValue };
      else if (key === 'leftCylinder' && value !== '') leftRx = { ...leftRx, cylinder: rxValue };
      else if (key === 'rightAxis' && value !== '') rightRx = { ...rightRx, axis: rxValue };
      else if (key === 'leftAxis' && value !== '') leftRx = { ...leftRx, axis: rxValue };
      else if (key === 'rightAdd' && rxValue) rightRx = { ...rightRx, addition: rxValue };
      else if (key === 'leftAdd' && rxValue) leftRx = { ...leftRx, addition: rxValue };
      else if (key === 'rightPd') pdData = { ...pdData, right: rxValue };
      else if (key === 'leftPd') pdData = { ...pdData, left: rxValue };
      else if (key === 'singlePd') pdData = { ...pdData, single: rxValue };
    }

    return { left: leftRx, right: rightRx, pupillaryDistance: pdData };
  };

  const savePrescription = () => {
    orderItemId = null;
    const pres = updatePrescription();

    setRx(pres);

    if (isRxOnly) {
      isFormSubmitted.current = true;
    }
  };

  const handleChange = () => {
    // standard rx rules validation
    const pres = updatePrescription();
    const { left, right } = pres;

    let valid = true;
    let errorList: string[] = [];
    let isSphUnusual = false;

    if ((left.sphere || 0) * (right.sphere || 0) < 0) {
      errorList = [
        ...errorList,
        "That's unusual! For most people, Sphere is either Negative (-) or positive (+). Are you sure your prescription shows both? For help, please contact customer support.",
      ];
      isSphUnusual = true;
    }

    if ((left.cylinder || 0) * (right.cylinder || 0) < 0) {
      errorList = [
        ...errorList,
        "That's unusual! For most people, Cylinder is either Negative (-) or positive (+). Are you sure your prescription shows both? For help, please contact customer support.",
      ];
    }

    if (
      (right.cylinder !== undefined && right.cylinder != 0 && right.axis === undefined) ||
      (left.cylinder !== undefined && left.cylinder != 0 && left.axis === undefined) ||
      (right.axis !== undefined && right.axis != 0 && right.cylinder === undefined) ||
      (left.axis !== undefined && left.axis != 0 && left.cylinder === undefined)
    ) {
      errorList = [...errorList, 'Prescription is not valid.'];
      valid = false;
    }

    if (right.addition !== left.addition) {
      errorList = [...errorList, 'Addition is unusual'];
    }

    if (isSphUnusual) {
      errorList = [...errorList, 'Unusual Prescription is not valid.'];
    }

    isFormSubmitted.current = false;

    setValidity(valid);
    setError(errorList);
  };

  const handleLensSelected = e => {
    setLens({
      code: 0,
      title: e.title,
      addOnPrice: e.price,
    });
  };

  return (
    <div className="lens-builder__section">
      <StepsIndicator count={isRxOnly ? 2 : 3} step={isRxOnly ? 2 : 3}></StepsIndicator>
      <div className="lens-builder__step">
        <h2 className="header">FILL THE PRESCRIPTION FORM</h2>
        <form className="rx" ref={el => (formRef = el as HTMLFormElement)}>
          <div className="rx__wrapper">
            <div className="rx__col rx__col--side"></div>
            <div className="rx__col">
              <label>Sphere</label>
              <OverlayTrigger
                key="top"
                placement="top"
                overlay={<Tooltip id={`tooltip-$top`}>{tooltip.sphere}</Tooltip>}
              >
                <a className="tooltip-btn" title=""></a>
              </OverlayTrigger>
            </div>
            <div className="rx__col">
              <label>Cylinder</label>
              <OverlayTrigger
                key="top"
                placement="top"
                overlay={<Tooltip id={`tooltip-$top`}>{tooltip.cylinder}</Tooltip>}
              >
                <a className="tooltip-btn" title=""></a>
              </OverlayTrigger>
            </div>
            <div className="rx__col">
              <label>Axis</label>
              <OverlayTrigger
                key="top"
                placement="top"
                overlay={<Tooltip id={`tooltip-$top`}>{tooltip.axis}</Tooltip>}
              >
                <a className="tooltip-btn" title=""></a>
              </OverlayTrigger>
            </div>
            {isProgressive && (
              <div className="rx__col">
                <label>Addition</label>
              </div>
            )}
          </div>
          {['right', 'left'].map(eye => {
            const rx = ((prescription || {}) as any)[eye] || {};
            return (
              <div className="rx__wrapper">
                <div className="rx__col rx__col--side">
                  <label>
                    <span>{eye}</span> eye:
                  </label>
                </div>
                <div className="rx__col">
                  <RxInput
                    id={`${eye}Sphere`}
                    min={-12}
                    max={12}
                    increment={0.25}
                    defaultValue={rx?.sphere || 0}
                    changed={handleChange}
                  />
                </div>
                <div className="rx__col">
                  <RxInput
                    id={`${eye}Cylinder`}
                    min={-6}
                    max={6}
                    increment={0.25}
                    placeholder="None"
                    defaultValue={rx?.cylinder || ''}
                    changed={handleChange}
                  />
                </div>
                <div className="rx__col">
                  <RxInput
                    id={`${eye}Axis`}
                    min={0}
                    max={180}
                    increment={1}
                    hasSign={false}
                    placeholder="None"
                    defaultValue={rx?.axis || ''}
                    changed={handleChange}
                  />
                </div>
                {isProgressive && (
                  <div className="rx__col">
                    <RxInput
                      id={`${eye}Add`}
                      min={0.75}
                      max={3.5}
                      increment={0.25}
                      placeholder="None"
                      defaultValue={rx?.addition || (isRxOnly ? '' : 0.75)}
                      changed={handleChange}
                    />
                  </div>
                )}
              </div>
            );
          })}
          <div className="rx__wrapper rx__wrapper--pd">
            <div className="rx__col">
              <label>Pupillary</label>
              <OverlayTrigger
                key="top"
                placement="top"
                overlay={
                  <Tooltip id={`tooltip-$top`} className="description">
                    {tooltip.pupillary}
                  </Tooltip>
                }
              >
                <a className="tooltip-btn" title=""></a>
              </OverlayTrigger>
            </div>
          </div>
          <div className="rx__wrapper">
            {pdType === 'doublePd' ? (
              <>
                <div className="rx__col rx__col--side">
                  <label>Right eye:</label>
                </div>
                <div className="rx__col rx__col--pd">
                  <RxInput
                    id="rightPd"
                    min={17}
                    max={45}
                    increment={0.25}
                    defaultValue={pd?.right || 31}
                    hasSign={false}
                    changed={handleChange}
                  />
                </div>
                <div className="rx__col rx__col--side">
                  <label>Left eye:</label>
                </div>
                <div className="rx__col rx__col--pd">
                  <RxInput
                    id="leftPd"
                    min={17}
                    max={45}
                    increment={0.25}
                    defaultValue={pd?.left || 31}
                    hasSign={false}
                    changed={handleChange}
                  />
                </div>
              </>
            ) : (
              <div className="rx__col rx__col--pd">
                <RxInput
                  id="singlePd"
                  min={35}
                  max={90}
                  increment={0.25}
                  defaultValue={pd?.single || 62}
                  hasSign={false}
                  changed={handleChange}
                />
              </div>
            )}
          </div>
          <div className="rx__wrapper">
            <div className="pd-selection">
              <input
                type="radio"
                name="pdType"
                checked={'singlePd' === pdType}
                onChange={() => setPdType('singlePd')}
              />
              <span>Single PD</span>
            </div>
            <div className="pd-selection">
              <input
                type="radio"
                name="pdType"
                checked={'doublePd' === pdType}
                onChange={() => setPdType('doublePd')}
              />
              <span>Double PD</span>
            </div>
          </div>
        </form>
        <ul className="rx-error">
          {errors.map(e => (
            <li>{e}</li>
          ))}
        </ul>
        {isRxOnly && (
          <IrlTool
            isQuizOnly={true}
            isQuizShow={isFormSubmitted.current}
            prescription={rx}
            lensSelected={handleLensSelected}
          />
        )}
      </div>
      <div className="cta-container">
        {!isRxOnly ? (
          <Button click={() => dispatch(orderSlice.actions.setStep(LensBuilderType.LENS_PACKAGE))}>
            GO BACK TO LENS
          </Button>
        ) : (
          <Button click={() => dispatch(orderSlice.actions.setStep(LensBuilderType.VISION_NEED))}>
            GO BACK TO VISION
          </Button>
        )}

        {isValid &&
          (!isRxOnly ? (
            <Button className="button secondary-button" click={savePrescription}>
              {orderItemId ? 'Update' : 'Add To Cart'}
            </Button>
          ) : (
            <Button className="button secondary-button" click={savePrescription}>
              Questionnaire
            </Button>
          ))}
      </div>
    </div>
  );
};

export default Prescription;
